import { z } from 'zod';
import { isoDateSchema } from './people.js';

export const ASSIGNMENT_STATUS = [
  'DRAFT',
  'PUBLISHED',
  'INVITED',
  'CONFIRMED',
  'DECLINED',
  'REPLACED',
  'CANCELLED',
  'PRESENT',
  'ABSENT',
] as const;
export const assignmentStatusSchema = z.enum(ASSIGNMENT_STATUS);
export type AssignmentStatus = z.infer<typeof assignmentStatusSchema>;

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatus, string> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicada',
  INVITED: 'Convocada',
  CONFIRMED: 'Confirmada',
  DECLINED: 'Recusada',
  REPLACED: 'Substituída',
  CANCELLED: 'Cancelada',
  PRESENT: 'Presente',
  ABSENT: 'Ausente',
};

/** As cores semânticas do produto (seção 9.2), amarradas a cada estado. */
export const ASSIGNMENT_STATUS_TONE: Record<
  AssignmentStatus,
  'rascunho' | 'convocado' | 'aguardando' | 'confirmado' | 'recusado' | 'substituido'
> = {
  DRAFT: 'rascunho',
  PUBLISHED: 'convocado',
  INVITED: 'aguardando',
  CONFIRMED: 'confirmado',
  DECLINED: 'recusado',
  REPLACED: 'substituido',
  CANCELLED: 'rascunho',
  PRESENT: 'confirmado',
  ABSENT: 'recusado',
};

export const WARNING_TYPES = [
  'sem_habilitacao',
  'habilitacao_vencida',
  'aprendiz',
  'data_bloqueada',
  'conflito_de_horario',
  'limite_do_mes',
  'limite_da_semana',
  'nao_costuma_servir',
  'par_proibido',
  'sem_liberacao',
  'inativa',
] as const;

export const warningSchema = z.object({
  tipo: z.enum(WARNING_TYPES),
  severidade: z.enum(['impeditivo', 'atencao']),
  mensagem: z.string(),
});
export type SchedulingWarning = z.infer<typeof warningSchema>;

export const candidateSchema = z.object({
  personId: z.string().uuid(),
  nome: z.string(),
  fotoUrl: z.string().nullable(),
  nivel: z.enum(['APPRENTICE', 'QUALIFIED', 'REFERENCE']).nullable(),
  avisos: z.array(warningSchema),
  elegivel: z.boolean(),
  pontuacao: z.number().int(),
  escalasNoMes: z.number().int(),
  diasDesdeAUltima: z.number().int().nullable(),
  /** Por que esta pessoa aparece aqui (M07): a mesma frase da auto-escala. */
  justificativa: z.string(),
});
export type Candidate = z.infer<typeof candidateSchema>;

export const assignmentSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventPositionId: z.string().uuid(),
  personId: z.string().uuid(),
  personName: z.string(),
  personPhotoUrl: z.string().nullable(),
  functionId: z.string().uuid(),
  functionName: z.string(),
  ministryId: z.string().uuid(),
  ministryName: z.string(),
  status: assignmentStatusSchema,
  origin: z.enum(['MANUAL', 'AUTO', 'SWAP', 'SUBSTITUTION']),
  eventStartsAt: z.string().datetime(),
  notes: z.string().nullable(),
  overrideReason: z.string().nullable(),
  declineReason: z.string().nullable(),
  confirmedAt: z.string().datetime().nullable(),
});
export type Assignment = z.infer<typeof assignmentSchema>;

export const createAssignmentSchema = z.object({
  eventPositionId: z.string().uuid('Escolha a vaga'),
  personId: z.string().uuid('Escolha a pessoa'),
  notes: z.string().trim().max(300).nullish(),
  /**
   * Escalar mesmo com aviso (data bloqueada, sobrecarga). Impeditivo continua
   * impeditivo: nem forçando alguém serve em dois lugares ao mesmo tempo.
   */
  override: z.boolean().default(false),
  overrideReason: z.string().trim().max(300).nullish(),
});
export type CreateAssignmentInput = z.infer<typeof createAssignmentSchema>;

export const changeAssignmentStatusSchema = z.object({
  status: assignmentStatusSchema,
  reason: z.string().trim().max(300).nullish(),
});
export type ChangeAssignmentStatusInput = z.infer<typeof changeAssignmentStatusSchema>;

export const publishScheduleSchema = z.object({
  /** Publica tudo o que está em rascunho no período. */
  from: isoDateSchema,
  to: isoDateSchema,
  ministryId: z.string().uuid().nullish(),
  eventId: z.string().uuid().nullish(),
});
export type PublishScheduleInput = z.infer<typeof publishScheduleSchema>;

export const publishResultSchema = z.object({
  publicadas: z.number().int(),
  eventos: z.number().int(),
  pessoas: z.number().int(),
  /** Eventos que ficaram com vaga essencial aberta, para o líder decidir. */
  eventosComVagaAberta: z.array(
    z.object({ id: z.string(), title: z.string(), localDate: z.string(), abertas: z.number().int() }),
  ),
  /** Departamentos que exigem aprovação: a escala vai para a liderança. */
  aguardandoAprovacao: z.array(z.object({ ministryId: z.string(), ministryName: z.string() })),
});
export type PublishResult = z.infer<typeof publishResultSchema>;

export const duplicateWeekSchema = z.object({
  /** Semana de origem (qualquer data dentro dela). */
  fromWeek: isoDateSchema,
  /** Semana de destino. */
  toWeek: isoDateSchema,
  ministryId: z.string().uuid().nullish(),
});
export type DuplicateWeekInput = z.infer<typeof duplicateWeekSchema>;

export const duplicateResultSchema = z.object({
  copiadas: z.number().int(),
  ignoradas: z.number().int(),
  motivos: z.array(z.object({ pessoa: z.string(), motivo: z.string() })),
});
export type DuplicateResult = z.infer<typeof duplicateResultSchema>;

/** Uma célula da grade: função × data. */
export const gridCellSchema = z.object({
  eventId: z.string().uuid(),
  eventPositionId: z.string().uuid().nullable(),
  quantity: z.number().int(),
  assignments: z.array(
    z.object({
      id: z.string().uuid(),
      personId: z.string().uuid(),
      personName: z.string(),
      status: assignmentStatusSchema,
    }),
  ),
});
export type GridCell = z.infer<typeof gridCellSchema>;

export const gridSchema = z.object({
  events: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      localDate: z.string(),
      localTime: z.string(),
      weekday: z.number().int(),
      status: z.string(),
    }),
  ),
  rows: z.array(
    z.object({
      functionId: z.string().uuid(),
      functionName: z.string(),
      ministryId: z.string().uuid(),
      ministryName: z.string(),
      ministryColor: z.string(),
      cells: z.record(z.string(), gridCellSchema),
    }),
  ),
  totals: z.object({
    necessario: z.number().int(),
    escalados: z.number().int(),
    confirmados: z.number().int(),
    rascunhos: z.number().int(),
  }),
});
export type Grid = z.infer<typeof gridSchema>;

export const gridQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  ministryId: z.string().uuid().optional(),
  congregationId: z.string().uuid().optional(),
});
export type GridQuery = z.infer<typeof gridQuerySchema>;

export const APPROVAL_STATUS = ['PENDING', 'APPROVED', 'RETURNED'] as const;
export const approvalStatusSchema = z.enum(APPROVAL_STATUS);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

export const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  PENDING: 'Aguardando aprovação',
  APPROVED: 'Aprovada',
  RETURNED: 'Devolvida para ajuste',
};

export const approvalSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  eventDate: z.string(),
  ministryId: z.string().uuid().nullable(),
  ministryName: z.string().nullable(),
  status: approvalStatusSchema,
  comment: z.string().nullable(),
  requestedAt: z.string().datetime(),
  decidedAt: z.string().datetime().nullable(),
});
export type Approval = z.infer<typeof approvalSchema>;

export const decideApprovalSchema = z.object({
  status: z.enum(['APPROVED', 'RETURNED']),
  comment: z.string().trim().max(500).nullish(),
});
export type DecideApprovalInput = z.infer<typeof decideApprovalSchema>;

/** Painel de carga por pessoa, para enxergar sobrecarga e ociosidade (D05). */
export const workloadSchema = z.object({
  personId: z.string().uuid(),
  personName: z.string(),
  photoUrl: z.string().nullable(),
  escalas: z.number().int(),
  confirmadas: z.number().int(),
  recusadas: z.number().int(),
  ultimaEscala: z.string().datetime().nullable(),
  diasDesdeAUltima: z.number().int().nullable(),
});
export type Workload = z.infer<typeof workloadSchema>;

export const workloadQuerySchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  ministryId: z.string().uuid().optional(),
});
export type WorkloadQuery = z.infer<typeof workloadQuerySchema>;

/** DELETE costuma vir sem corpo: o motivo é bem-vindo, não obrigatório. */
export const removeAssignmentSchema = z
  .object({
    reason: z.string().trim().max(300).nullish(),
  })
  .default({});
export type RemoveAssignmentInput = z.infer<typeof removeAssignmentSchema>;
